import { ChartData } from 'chart.js';
import {
  PiPointDictionary,
  PiDataPoint,
  PiPoints,
} from '../../app/models/pi-data.model';
import { PALETTE, toTransparent } from '../color.utils';

export interface XyDataPoint {
  x: number;
  y: number;
}

export class PiDataPointsDatasetConverter {
  public static removePointFromChart(
    chartData: ChartData<'line'>,
    pointTag: string
  ): ChartData<'line'> {
    const updatedDatasets = chartData.datasets.filter(
      dataset => !dataset.label?.includes(`(${pointTag})`)
    );
    
    return {
      ...chartData,
      datasets: updatedDatasets
    };
  }

  public static addPointToChart(
    existingChartData: ChartData<'line'>,
    newPiPoints: PiPoints,
    newPiPointDetails: PiPointDictionary
  ): ChartData<'line'> {
    const newChartData = this.getChartData(newPiPoints, newPiPointDetails);
    return {
      labels: [],
      datasets: [...existingChartData.datasets, ...newChartData.datasets],
    };
  }

  public static getChartData(
    piPoints: PiPoints,
    piPointDetails: PiPointDictionary
  ): ChartData<'line'> {
    const datasetsMap = new Map<
      string,
      {
        data: XyDataPoint[];
        borderColor: string;
        backgroundColor: string;
        label: string;
        unit: string;
        valueLabels: {} | undefined;
      }
    >();

    const convertTextToNumber = (values: any[]): { convertedValues: number[], valueLabels?: Record<number, string> } => {
      const allStrings = Array.isArray(values) && values.every((v: any) => typeof v === 'string');
      const textValues = values.filter(v => isNaN(Number(v)));
      
      if (textValues.length === 0) {
        return { convertedValues: values.map(v => Number(v)) };
      }
      
      const uniqueTexts = [...new Set(textValues)].sort();
      const textToNumberMap = new Map<string, number>();
      const valueLabels: Record<number, string> = {};
      
      uniqueTexts.forEach((text, index) => {
        const numValue = index * 100;
        textToNumberMap.set(text, numValue);
        valueLabels[numValue] = text;
      });
      
      const convertedValues = values.map(v => {
        if (isNaN(Number(v))) {
          return textToNumberMap.get(v) || 0;
        }
        return Number(v);
      });
      
      return {
        convertedValues,
        valueLabels: allStrings ? valueLabels : undefined
      };
    };

    piPoints.points.forEach((dataPoint: PiDataPoint) => {
      const pointDetails = Object.values(piPointDetails).find(
        (pd) => pd?.Name === dataPoint.tag
      );
      const datasetLabel = `${pointDetails?.Descriptor} (${pointDetails?.Name})`;

      const { convertedValues, valueLabels } = convertTextToNumber(dataPoint.values);
      
      if (!datasetsMap.has(datasetLabel)) {
        const colorIndex = datasetsMap.size % PALETTE.length;
        const color = PALETTE[colorIndex];
        
        datasetsMap.set(datasetLabel, {
          data: [],
          unit: pointDetails?.EngineeringUnits || '',
          borderColor: color,
          backgroundColor: toTransparent(color),
          label: datasetLabel,
          valueLabels
        });
      }
      
      dataPoint.ts.forEach((timestamp, index) => {
        const dataset = datasetsMap.get(datasetLabel)!;
        if (index < convertedValues.length) {
          dataset.data.push({ x: new Date(timestamp).getTime(), y: convertedValues[index] });
        }
      });
    });

    // Sort each dataset by x (required for parsing:false + decimation)
    const datasets = Array.from(datasetsMap.values()).map((dataset) => ({
      ...dataset,
      data: dataset.data.sort((a, b) => a.x - b.x),
      fill: false,
      borderWidth: 2,
      tension: 0.4,
      cubicInterpolationMode: 'monotone',
      parsing: false as const,
      normalized: true,
    }));

    return {
      labels: [],
      datasets,
    } as unknown as ChartData<'line'>;
  }
}
